import { useEffect, useMemo, useState } from 'react';
import Modal from 'antd/lib/modal';
import Skeleton from 'antd/lib/skeleton';
import message from 'antd/lib/message';
import { getPublicAnnouncementByIdentifier, listPublicAnnouncements } from '../../api/announcements';
import { mapApiAnnouncementToCard } from '../../api/announcements.mappers';
import AnnouncementCard from './AnnouncementCard';

function toTimestamp(value) {
  const parsed = new Date(value || '').getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function AnnouncementsSection({ loadingSkeleton = false }) {
  const [announcementItems, setAnnouncementItems] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(true);
  const [allOpen, setAllOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const latestAnnouncements = useMemo(() => announcementItems.slice(0, 3), [announcementItems]);
  const remainingAnnouncements = useMemo(() => announcementItems.slice(3), [announcementItems]);

  const handleOpenAnnouncement = async (announcement, identifier) => {
    setOpen(true);
    setSelectedAnnouncement({
      title: announcement.title,
      summary: announcement.description,
      content: announcement.content || announcement.description,
      priority: announcement.priority || 'MEDIUM',
    });

    if (!identifier) return;

    setModalLoading(true);
    try {
      const full = await getPublicAnnouncementByIdentifier(identifier);
      setSelectedAnnouncement(full);
    } catch {
      message.error('Unable to load full announcement details.');
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    async function loadAnnouncements() {
      setLoadingAnnouncements(true);
      try {
        const response = await listPublicAnnouncements({
          page: 1,
          pageSize: 25,
          status: 'PUBLISHED',
          sortBy: 'createdAt',
          sortOrder: 'desc',
        });

        const sortedByCreatedAt = [...response].sort((first, second) => {
          const secondCreatedAt =
            toTimestamp(second?.createdAt) ||
            toTimestamp(second?.publishedAt) ||
            toTimestamp(second?.updatedAt);
          const firstCreatedAt =
            toTimestamp(first?.createdAt) ||
            toTimestamp(first?.publishedAt) ||
            toTimestamp(first?.updatedAt);
          return secondCreatedAt - firstCreatedAt;
        });

        const mapped = sortedByCreatedAt.map(mapApiAnnouncementToCard).filter((item) => item.title);
        setAnnouncementItems(mapped);
      } catch {
        setAnnouncementItems([]);
      } finally {
        setLoadingAnnouncements(false);
      }
    }

    loadAnnouncements();
  }, []);

  const showLoading = loadingSkeleton || loadingAnnouncements;
  const hideSection = !showLoading && announcementItems.length === 0;

  if (hideSection) return null;

  return (
    <section className="announcements" aria-labelledby="announcements-title">
      <div className="announcements__heading">
        <h2 id="announcements-title">Announcements</h2>
        {!showLoading && remainingAnnouncements.length ? (
          <button type="button" className="announcements__see-all" onClick={() => setAllOpen(true)}>
            See all
          </button>
        ) : null}
      </div>

      <div className="announcements__grid">
        {showLoading ? (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <article key={`announcement-skeleton-${index}`} className="announcement-skeleton-card">
                <Skeleton active avatar={{ shape: 'square', size: 30 }} title={{ width: '62%' }} paragraph={{ rows: 2 }} />
              </article>
            ))}
          </>
        ) : latestAnnouncements.length ? (
          latestAnnouncements.map((item) => (
            <AnnouncementCard key={item.id} announcement={item} onOpen={handleOpenAnnouncement} />
          ))
        ) : null}
      </div>

      <Modal title="All Announcements" open={allOpen} onCancel={() => setAllOpen(false)} footer={null}>
        <ul className="popular-card__list announcements__list">
          {remainingAnnouncements.map((item) => {
            const identifier = item.identifier || item.id;
            return (
              <li key={item.id} className="announcements__list-item">
                <button
                  type="button"
                  className="announcements__list-trigger"
                  onClick={() => {
                    setAllOpen(false);
                    handleOpenAnnouncement(item, identifier);
                  }}
                >
                  <span className="announcements__list-title">{item.title}</span>
                  <span className="announcements__list-desc">{item.description}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </Modal>

      <Modal
        title={selectedAnnouncement?.title || 'Announcement'}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={680}
        wrapClassName="announcement-detail-modal"
      >
        {modalLoading ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : (
          <div className="announcement-modal">
            <p className="announcement-modal__meta">{selectedAnnouncement?.priority || 'MEDIUM'}</p>
            <p className="announcement-modal__summary">{selectedAnnouncement?.summary || ''}</p>
            <p className="announcement-modal__content">
              {selectedAnnouncement?.content || selectedAnnouncement?.summary || ''}
            </p>
          </div>
        )}
      </Modal>
    </section>
  );
}

export default AnnouncementsSection;
